import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const stats = [
  { value: '+300', label: 'Festas Realizadas' },
  { value: '80k', label: 'Convidados Atendidos' },
  { value: '99%', label: 'Clientes Satisfeitos' },
  { value: '+50', label: 'Cidades no Brasil' },
];

const highlights = [
  {
    icon: 'ri-headphone-line',
    title: 'Vários DJs ao Vivo',
    description: 'Até 10 canais independentes de música. Cada convidado escolhe qual DJ ouvir direto no fone — e dança com quem está ouvindo o mesmo canal. Os LEDs coloridos identificam quem está em cada frequência.',
  },
  {
    icon: 'ri-volume-up-line',
    title: 'Volume Individual',
    description: 'Cada pessoa ajusta o volume do seu fone. Quem gosta de som alto ouve alto. Quem prefere conversar mantém baixo. Todo mundo feliz, do começo ao fim da festa.',
  },
  {
    icon: 'ri-moon-clear-line',
    title: 'Sem Limite de Horário',
    description: 'Sem caixas de som, sem barulho para a vizinhança. A festa pode continuar madrugada adentro sem nenhuma reclamação. Ideal para casamentos, formaturas e eventos em áreas residenciais.',
  },
];

export default function WhatIsSection() {
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: highlightsRef, isVisible: highlightsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <>
      {/* Stats strip */}
      <section ref={statsRef} className="py-14 bg-secondary">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div
                key={i}
                className={`text-center transition-all duration-500 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="font-heading font-extrabold text-4xl md:text-5xl text-primary mb-2">{stat.value}</div>
                <div className="font-body text-sm text-white/70 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is silent party */}
      <section ref={introRef} className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div className={`transition-all duration-700 ${introVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
                O que é
              </span>
              <h2 className="font-heading font-bold text-5xl text-secondary mb-6 leading-tight">
                festa silenciosa<br />silent disco
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-6">
                <strong className="text-secondary">Festa Silenciosa</strong> — ou <strong className="text-secondary">Silent Disco</strong> — é um formato de festa onde a música chega direto ao ouvido de cada convidado por fones sem fio, em vez de caixas de som tradicionais.
              </p>
              <p className="font-body text-base text-gray-600 leading-relaxed mb-6">
                Com <strong>vários DJs tocando simultaneamente</strong> em canais diferentes, cada convidado escolhe qual música ouvir. Os fones têm LEDs coloridos que mostram qual canal cada pessoa está ouvindo — criando um espetáculo visual incrível na pista de dança.
              </p>
              <p className="font-body text-base text-gray-600 leading-relaxed">
                Perfeito para <strong>casamentos</strong>, formaturas, festas de aniversário, eventos corporativos, festivais e qualquer celebração onde você quer música de qualidade sem perturbar a vizinhança.
              </p>
            </div>

            {/* Image */}
            <div className={`relative transition-all duration-700 delay-200 ${introVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="w-full h-96 rounded-3xl overflow-hidden">
                <img
                  src="/img/stib/festa-silent-disco.webp"
                  alt="Convidado usando fone LED em festa silenciosa Silent Disco"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10">
                    <i className="ri-music-line text-primary text-2xl" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-secondary text-sm">Até 10 Canais Simultâneos</div>
                    <div className="font-body text-xs text-gray-500">LED colorido por canal</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section ref={highlightsRef} className="py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
              Diferenciais
            </span>
            <h2 className={`font-heading font-bold text-5xl text-secondary transition-all duration-700 ${highlightsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              por que silent disco
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((item, i) => (
              <div
                key={i}
                className={`bg-white rounded-3xl p-10 border border-gray-100 hover:border-primary/20 transition-all duration-500 ${highlightsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary/10 mb-6">
                  <i className={`${item.icon} text-primary text-3xl`} />
                </div>
                <h3 className="font-heading font-bold text-xl text-secondary mb-4">{item.title}</h3>
                <p className="font-body text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}